import React, { useState, useEffect } from 'react';
import { AlertTriangle, X } from 'lucide-react';

/**
 * DangerConfirmModal — reusable confirmation modal for destructive admin actions.
 *
 * Props:
 *   open           – controls visibility
 *   onClose        – called when the user dismisses the modal
 *   onConfirm      – called with { confirmation_phrase, acknowledged } when valid
 *   title          – e.g. "Delete All Prospects"
 *   description    – explains what will happen
 *   confirmPhrase  – exact string the user must type (e.g. "DELETE ALL PROSPECTS")
 *   loading        – disables the button while the request is in-flight
 *   requireDryRun  – if true, the Confirm button is disabled until dryRunCompleted is true
 *   dryRunCompleted – whether a dry run was completed in this session
 */
export const DangerConfirmModal = ({
  open,
  onClose,
  onConfirm,
  title,
  description,
  confirmPhrase,
  loading = false,
  requireDryRun = false,
  dryRunCompleted = true,
}) => {
  const [typed, setTyped] = useState('');
  const [ack, setAck] = useState(false);

  useEffect(() => {
    if (open) { setTyped(''); setAck(false); }
  }, [open]);

  if (!open) return null;

  const phraseMatch = typed.trim() === confirmPhrase;
  const canConfirm = phraseMatch && ack && (!requireDryRun || dryRunCompleted) && !loading;
  const dryRunBlocked = requireDryRun && !dryRunCompleted;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm" data-testid="danger-confirm-overlay">
      <div className="bg-[#1a1a2e] border border-red-500/40 rounded-xl shadow-2xl w-full max-w-lg mx-4 p-0 overflow-hidden" data-testid="danger-confirm-modal">
        {/* Header */}
        <div className="flex items-center gap-3 px-6 py-4 bg-red-500/10 border-b border-red-500/20">
          <AlertTriangle className="w-6 h-6 text-red-400 shrink-0" />
          <h2 className="text-lg font-bold text-red-300 flex-1">{title}</h2>
          <button onClick={onClose} className="text-white/40 hover:text-white transition-colors" data-testid="danger-confirm-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-5">
          <p className="text-sm text-white/70 leading-relaxed whitespace-pre-line">{description}</p>

          {dryRunBlocked && (
            <div className="px-4 py-3 rounded-lg bg-yellow-500/10 border border-yellow-500/30 text-yellow-300 text-sm" data-testid="danger-dry-run-warning">
              You must complete a Dry Run in this session before running the live action.
            </div>
          )}

          {/* Confirmation phrase input */}
          <div>
            <label className="block text-xs font-semibold text-white/50 uppercase tracking-wider mb-2">
              Type <span className="text-red-400 font-mono">{confirmPhrase}</span> to confirm
            </label>
            <input
              type="text"
              value={typed}
              onChange={e => setTyped(e.target.value)}
              placeholder={confirmPhrase}
              className="w-full px-4 py-2.5 bg-black/40 border border-white/10 rounded-lg text-white text-sm font-mono focus:outline-none focus:border-red-500/50 placeholder:text-white/20"
              autoComplete="off"
              spellCheck={false}
              data-testid="danger-confirm-phrase-input"
            />
            {typed.length > 0 && !phraseMatch && (
              <p className="mt-1 text-xs text-red-400">Phrase does not match</p>
            )}
          </div>

          {/* Acknowledgment checkbox */}
          <label className="flex items-start gap-3 cursor-pointer select-none group">
            <input
              type="checkbox"
              checked={ack}
              onChange={e => setAck(e.target.checked)}
              className="mt-0.5 w-4 h-4 rounded border-white/20 accent-red-500"
              data-testid="danger-confirm-acknowledge"
            />
            <span className="text-sm text-white/60 group-hover:text-white/80 transition-colors">
              I understand this changes production data and may not be reversible.
            </span>
          </label>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 bg-black/20 border-t border-white/5">
          <button
            onClick={onClose}
            className="px-5 py-2 rounded-lg text-sm text-white/60 hover:text-white hover:bg-white/5 transition-colors"
            data-testid="danger-confirm-cancel"
          >
            Cancel
          </button>
          <button
            onClick={() => onConfirm({ confirmation_phrase: typed.trim(), acknowledged: ack })}
            disabled={!canConfirm}
            className={`px-5 py-2 rounded-lg text-sm font-semibold transition-all ${
              canConfirm
                ? 'bg-red-600 hover:bg-red-500 text-white shadow-lg shadow-red-500/20'
                : 'bg-red-900/30 text-red-300/40 cursor-not-allowed'
            }`}
            data-testid="danger-confirm-execute"
          >
            {loading ? 'Executing...' : 'Confirm & Execute'}
          </button>
        </div>
      </div>
    </div>
  );
};
